import React, { useContext, useEffect, useState } from 'react';

import { Add, Check, Close, Delete, Edit, Search } from '@mui/icons-material';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import TextField from '@mui/material/TextField';

import { LayerContext } from 'state/LayerContext';
import { useGetLayerQuery } from 'state/apiSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  SavedRoute,
  SavedRouteList,
  selectInSavedRouteEditMode,
  setDisplayedSavedRoute,
  setInSavedRouteEditMode,
  useGetSavedRoutesQuery,
  useSavedRouteCreateMutation,
  useSavedRouteDeleteMutation,
  useSavedRouteUpdateMutation,
} from 'state/savedRouteSlices';
import { getProjectInfoForSlug, selectUser } from 'state/userSlice';

import { SOMETHING_WENT_WRONG_SAVING_MSG, getSavedSuccessMsg } from 'appUtils';
import { ConfirmDeletePopup } from 'features/common/ConfirmDeletePopup';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { StlButton } from 'features/common/StlButton/StlButton';
import StlHelperTxt from 'features/common/StlHelperTxt';
import StlTooltip from 'features/common/StlTooltip';
import { canManageSavedRoute } from 'features/common/savedRouteUtils';
import { showToast } from 'features/common/toasts/ToastProvider';
import { classNames, updateNameError } from 'features/common/utils';

import {
  selectProject,
  selectRouteSelection,
  setMapCenter,
  setRouteSelection,
  setSelectedSegmentId,
} from '../../state/workflowSlice';
import { useRouteInfo } from '../common/hooks/useRouteInfo';
import { jsonTupleToLatlng } from '../map/utils/geography_utils';
import { SavedRoutesVariant } from './utils';

import styles from './SavedRoutesToolbar.module.css';

interface ISavedRouteToolbar {
  modern?: boolean;
  variant?: SavedRoutesVariant;
}

export function SavedRoutesToolbar({
  modern = false,
  variant = 'regular',
}: ISavedRouteToolbar) {
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [editSavedRouteName, setEditSavedRouteName] = useState('');
  const [editSavedRouteId, setEditSavedRouteId] = useState(undefined);
  const [deleteSavedRouteId, setDeleteSavedRouteId] = useState(undefined);
  const [savedRoutes, setSavedRoutes] = useState<SavedRouteList>([]);
  const [pendingPanToRoute, setPendingPanToRoute] = useState<boolean>(false);
  const project_slug = useAppSelector(selectProject);
  const inSavedRouteEditMode = useAppSelector(selectInSavedRouteEditMode);
  const selectedRoute = useAppSelector(selectRouteSelection);
  const routeSelection = useAppSelector(selectRouteSelection);
  const user = useAppSelector(selectUser);
  const projectInfo = getProjectInfoForSlug(user, project_slug);
  const isSubAdmin = !!projectInfo?.is_sub_admin;

  const { layer } = useContext(LayerContext);

  const { currentData: savedRouteData } = useGetSavedRoutesQuery(project_slug, {
    skip: !project_slug,
  });

  const { savedRoute: savedRouteInfo, basicRouteInfo } = useRouteInfo();

  const [updateSavedRoute, { data: updateSavedRouteData }] =
    useSavedRouteUpdateMutation();
  const [createSavedRoute, { data: createSavedRouteData }] =
    useSavedRouteCreateMutation();
  const [deleteSavedRoute] = useSavedRouteDeleteMutation();

  useGetLayerQuery(layer, { skip: !layer });

  useEffect(() => {
    let routesToDisplay = savedRouteData?.savedRoutes || [];

    if (search && showSearch) {
      routesToDisplay = routesToDisplay.filter((value) =>
        value.routeName.toLowerCase().includes(search.toLowerCase()),
      );
    }

    setSavedRoutes(routesToDisplay);
  }, [savedRouteData, search, showSearch]);

  useEffect(() => {
    if (savedRouteInfo?.centerPoint) {
      if (pendingPanToRoute) {
        dispatch(
          setMapCenter(
            jsonTupleToLatlng(savedRouteInfo.centerPoint.coordinates as any),
          ),
        );
        setPendingPanToRoute(false);
      }
    }
  }, [savedRouteInfo]);

  const onEditClick = (event, index) => {
    const savedRoute = savedRoutes[index];
    dispatch(setRouteSelection(savedRoute.id));
    dispatch(
      setSelectedSegmentId(
        savedRoute.routeSegmentIds[savedRoute.routeSegmentIds.length - 1],
      ),
    );
    dispatch(setInSavedRouteEditMode(true));
    setEditSavedRouteName(savedRoute.routeName);
    setEditSavedRouteId(savedRoute.id);
    setPendingPanToRoute(true);
  };

  function exitEditMode() {
    dispatch(setInSavedRouteEditMode(false));
    setEditSavedRouteId(undefined);
    setEditSavedRouteName('');
  }

  const onEditSubmit = (event, index) => {
    const savedRoute = savedRoutes[index];
    console.assert(savedRoute.id === editSavedRouteId);
    const { waypointSegments, realizedSegments } = basicRouteInfo;
    updateSavedRoute({
      project_slug: project_slug as string,
      savedRouteId: editSavedRouteId,
      savedRoute: {
        routeName: editSavedRouteName,
        routeSegmentIds: waypointSegments,
        realizedSegments,
      },
    });
    exitEditMode();
  };

  useEffect(() => {
    const new_id = updateSavedRouteData?.id;
    if (new_id && new_id !== routeSelection) {
      dispatch(setRouteSelection(new_id));
    }
  }, [updateSavedRouteData]);

  const onAddClick = (event) => {
    hideSearch();
    dispatch(setDisplayedSavedRoute(undefined));
    dispatch(setInSavedRouteEditMode(true));
    setEditSavedRouteName('');
    setEditSavedRouteId(-1);
  };

  const onAddSubmit = (event) => {
    dispatch(setInSavedRouteEditMode(false));
    const { waypointSegments, realizedSegments } = basicRouteInfo;

    if (
      !editSavedRouteName ||
      !waypointSegments?.length ||
      !realizedSegments?.length
    )
      return;

    createSavedRoute({
      project_slug: project_slug as string,
      savedRoute: {
        routeName: editSavedRouteName,
        routeSegmentIds: waypointSegments,
        realizedSegments,
      },
    })
      .unwrap()
      .then(() => showToast('success', getSavedSuccessMsg('route')))
      .catch((e) => {
        console.error(e);
        showToast('error', SOMETHING_WENT_WRONG_SAVING_MSG);
      });
    exitEditMode();
  };

  useEffect(() => {
    const new_id = createSavedRouteData?.id;
    if (new_id && new_id !== routeSelection) {
      dispatch(setRouteSelection(new_id));
    }
  }, [createSavedRouteData]);

  const onSelect = (event, index) => {
    exitEditMode();

    const savedRoute = savedRoutes[index];
    if (routeSelection === savedRoute.id) {
      // deselect
      dispatch(setRouteSelection(undefined));
      dispatch(setSelectedSegmentId(undefined));
    } else {
      dispatch(setRouteSelection(savedRoute.id));
      dispatch(
        setSelectedSegmentId(
          savedRoute.routeSegmentIds[savedRoute.routeSegmentIds.length - 1],
        ),
      );
      setPendingPanToRoute(true);
    }
  };

  const onDeleteClick = (id: string) => {
    setDeleteSavedRouteId(id);
    exitEditMode();
  };

  const onDeleteSubmit = () => {
    if (!deleteSavedRouteId) return;
    deleteSavedRoute({
      project_slug: project_slug as string,
      savedRouteId: deleteSavedRouteId,
    });
    setDeleteSavedRouteId(undefined);
    if (routeSelection === deleteSavedRouteId) {
      dispatch(setRouteSelection(undefined));
      dispatch(setSelectedSegmentId(undefined));
    }
  };

  useEffect(() => {
    if (savedRouteInfo) {
      const route = savedRouteInfo.routeSegmentIds;
      dispatch(setSelectedSegmentId(route[route.length - 1]));
    }
  }, [savedRouteInfo]);

  function routeSelectedError() {
    if ((basicRouteInfo.waypointSegments || []).length === 0) {
      return 'Click map segments to draw route before saving';
    }
    return undefined;
  }

  const routeNameOnChange = (event) => {
    const { inputType } = event.nativeEvent as any;
    if (
      event.target !== document.activeElement &&
      (inputType === 'historyUndo' || inputType === 'historyRedo')
    ) {
      // Supress ctrl z if element is not focused
    } else {
      setEditSavedRouteName(event.target.value);
    }
  };

  const updateNameErrorInternal = (
    oldName: undefined | string,
    newName: string,
    editMode: boolean,
  ): string => {
    const existingNames = savedRoutes
      .filter((r) => !editMode || r.id !== editSavedRouteId)
      .map((r) => r.routeName);
    return updateNameError(oldName, newName, existingNames, editMode);
  };

  const routeSubmitErrors = (editMode: boolean) => {
    return [
      updateNameErrorInternal(undefined, editSavedRouteName, editMode),
      routeSelectedError(),
    ]
      .filter((v) => !!v)
      .join(', ');
  };

  function renderItem(savedRoute: SavedRoute, index) {
    const { routeName, id } = savedRoute;
    const isEditing = editSavedRouteId === id && inSavedRouteEditMode;
    const isDeleting = deleteSavedRouteId === id;
    const selected =
      (id === editSavedRouteId && isEditing) || id === routeSelection;
    const warn_of_crosswalk_issues =
      selected && savedRouteInfo?.had_issues_crosswalking;

    const canEditOrDelete = canManageSavedRoute(
      user?.email,
      savedRoute.creatorEmail,
      isSubAdmin,
    );
    const manageRouteDisabledReason =
      'Only the route creator or a project sub-admin can edit or delete this route.';
    let secondaryActions;
    let itemContent;

    if (!isEditing) {
      // Normal savedRoute view
      secondaryActions = (
        <>
          <StlTooltip
            title={canEditOrDelete ? '' : manageRouteDisabledReason}
            placement="top"
          >
            <span style={{ display: 'inline-flex' }}>
              <IconButton
                className={`${styles.bookmark_edit_button} ${styles.routes_button}`}
                disabled={!canEditOrDelete}
                edge="end"
                aria-label="edit"
                title="Edit"
                onClick={(event) => onEditClick(event, index)}
              >
                <Edit />
              </IconButton>
            </span>
          </StlTooltip>
          <StlTooltip
            title={canEditOrDelete ? '' : manageRouteDisabledReason}
            placement="top"
          >
            <span style={{ display: 'inline-flex' }}>
              <IconButton
                disabled={!canEditOrDelete}
                title="Delete"
                className={`${styles.bookmark_delete_button} ${styles.routes_button}`}
                onClick={() => onDeleteClick(id)}
              >
                <Delete />
              </IconButton>
            </span>
          </StlTooltip>
        </>
      );
      itemContent = (
        <ListItemButton
          title={routeName}
          className={
            modern ? styles.route_item_button_modern : styles.route_item_button
          }
          onClick={(event) => onSelect(event, index)}
        >
          <ListItemText
            primary={routeName}
            secondary={
              warn_of_crosswalk_issues ? (
                <div className={styles.saved_route_crosswalk_warning}>
                  <div className={styles.saved_route_crosswalk_warning_bang}>
                    !
                  </div>
                  <div className={styles.saved_route_crosswalk_warning_text}>
                    Could not update route due to changes in the road network.{' '}
                    <u id="route-crosswalk-error-warning">Learn more</u>
                  </div>
                </div>
              ) : undefined
            }
          />
        </ListItemButton>
      );
    } else {
      const editErrorString = routeSubmitErrors(true);
      // Editing view
      secondaryActions = (
        <>
          <IconButton
            className={styles.routes_button}
            edge="end"
            aria-label="submit edit"
            title="Submit edit"
            onClick={(event) => onEditSubmit(event, index)}
            disabled={!!editErrorString}
          >
            <Check />
          </IconButton>
          <IconButton
            className={styles.routes_button}
            edge="end"
            aria-label="cancel"
            title="Cancel"
            onClick={(event) => exitEditMode()}
          >
            <Close />
          </IconButton>
        </>
      );
      itemContent = (
        <TextField
          value={editSavedRouteName}
          onChange={routeNameOnChange}
          variant="standard"
          size="small"
          className={styles.edit_name_field}
          error={!!editErrorString}
          helperText={
            editErrorString ? (
              <StlHelperTxt variant="error" txt={editErrorString} />
            ) : null
          }
        />
      );
    }

    return (
      <ListItem
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (editSavedRouteId) onEditSubmit(e, index);
          }
        }}
        disablePadding
        secondaryAction={secondaryActions}
        selected={selected}
        className={classNames(
          selected ? styles.saved_route_item_selected : styles.saved_route_item,
          modern && styles.saved_route_modern,
        )}
        key={id}
        sx={
          modern
            ? {
                '& .MuiListItemSecondaryAction-root': {
                  right: 8,
                  display: 'flex',
                  gap: 0,
                },
              }
            : {}
        }
      >
        {itemContent}
        <ConfirmDeletePopup
          objectType="route"
          isOpen={Boolean(isDeleting)}
          setIsOpen={() => setDeleteSavedRouteId(undefined)}
          onApply={onDeleteSubmit}
          name={routeName}
        />
      </ListItem>
    );
  }
  const isAdding = inSavedRouteEditMode && editSavedRouteId === -1;
  const addingErrorMessage = routeSubmitErrors(false);
  const addingNameErrorMessage = updateNameErrorInternal(
    undefined,
    editSavedRouteName,
    false,
  );

  const onSearch = () => {
    dispatch(setInSavedRouteEditMode(false));
    setShowSearch(true);
  };

  const searchButton = (
    <IconButton onClick={onSearch} size="small" title="Search routes">
      <Search sx={modern ? { fontSize: '1rem' } : undefined} />
    </IconButton>
  );

  const headerNav = modern ? (
    <>
      <Typography variant="body1">Saved Routes</Typography>
      <Box display="flex" gap={0.5} justifyContent="space-between" width="100%">
        <StlButton
          gray
          isActive
          value="Add new"
          handleClick={onAddClick}
          icon={<Add />}
          width={88}
        />
        {searchButton}
      </Box>
    </>
  ) : (
    <>
      <IconButton
        onClick={onAddClick}
        size="small"
        title="Create a route"
        disabled={!selectedRoute}
      >
        <FontAwesomeIcon name="plus" sx={{ color: 'unset' }} />
      </IconButton>
      {searchButton}
    </>
  );

  const hideSearch = () => {
    setSearch('');
    setShowSearch(false);
  };

  return (
    <>
      <div
        className={classNames(
          modern ? styles.header_padded_modern : styles.header_padded,
          variant === 'modal' && styles.header_modal,
        )}
      >
        {(!showSearch || modern) && headerNav}
      </div>
      {showSearch && !isAdding && (
        <div
          className={
            modern ? styles.header_padded_modern : styles.header_padded
          }
        >
          <Box position="relative" width="100%">
            <TextField
              placeholder="Search saved route"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              variant="standard"
              size="small"
              className={styles.text_field}
            />
            <div className={styles.text_field_btn_group}>
              <IconButton
                className={styles.text_field_btn}
                onClick={hideSearch}
                size="small"
                title="Hide search"
              >
                <Close />
              </IconButton>
            </div>
          </Box>
        </div>
      )}
      {isAdding && !showSearch && (
        <div
          className={
            modern ? styles.header_padded_modern : styles.header_padded
          }
        >
          <Box position="relative" width="100%">
            <TextField
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onAddSubmit(e);
                }
              }}
              placeholder="Enter new route name"
              value={editSavedRouteName}
              onChange={routeNameOnChange}
              variant="standard"
              size="small"
              className={classNames(
                styles.text_field,
                styles.text_field_create,
              )}
              error={!!addingNameErrorMessage || !!addingErrorMessage}
              helperText={
                addingNameErrorMessage || addingErrorMessage ? (
                  <StlHelperTxt
                    variant="error"
                    txt={addingNameErrorMessage || addingErrorMessage}
                  />
                ) : null
              }
            />
            <div className={styles.text_field_btn_group}>
              <span style={{ display: 'flex' }}>
                <IconButton
                  className={styles.text_field_btn}
                  onClick={onAddSubmit}
                  disabled={
                    !!addingErrorMessage ||
                    !(editSavedRouteName?.trim().length > 0)
                  }
                  size="small"
                >
                  <Check />
                </IconButton>
              </span>
              <IconButton
                className={styles.text_field_btn}
                edge="end"
                aria-label="cancel"
                title="Cancel"
                onClick={(event) => {
                  dispatch(setInSavedRouteEditMode(false));
                }}
              >
                <Close />
              </IconButton>
            </div>
          </Box>
        </div>
      )}
      {savedRoutes && savedRoutes.length > 0 ? (
        <List
          className={`${styles.routes_toolbar_list} ${modern && styles.bookmark_toolbar_list}`}
        >
          {savedRoutes.map(renderItem)}
        </List>
      ) : (
        <div>
          <p className={styles.no_bookmarks_message}>No saved routes found</p>
          <p className={styles.no_bookmarks_message}>
            Select a route on the map and press + to add
          </p>
        </div>
      )}
    </>
  );
}
